import { useState, useEffect, useMemo } from "react";
import API from "@/lib/api";
import { QrCode, Download, Eye, Loader2, Plus, Search, FlaskConical, Copy, Check } from "lucide-react";

export default function AdminQRGenerator() {
  const [allProducts, setAllProducts] = useState([]);
  const [productSearch, setProductSearch] = useState("");
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [count, setCount] = useState(10);
  const [generating, setGenerating] = useState(false);
  const [batches, setBatches] = useState([]);
  const [viewBatch, setViewBatch] = useState(null);
  const [batchCodes, setBatchCodes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  useEffect(() => {
    // Pull all products once (catalog is small enough — ~260 reagents).
    API.get("/products?limit=1000").then(r => setAllProducts(r.data?.products || [])).catch(() => {});
    API.get("/admin/qr/batches").then(r => setBatches(r.data || [])).catch(() => {});
  }, []);

  const filtered = useMemo(() => {
    if (!productSearch) return allProducts.slice(0, 30);
    const q = productSearch.toLowerCase();
    return allProducts.filter(p =>
      (p.product_code || "").toLowerCase().includes(q) ||
      (p.name || "").toLowerCase().includes(q) ||
      (p.category || "").toLowerCase().includes(q)
    ).slice(0, 50);
  }, [allProducts, productSearch]);

  const generateQR = async () => {
    if (!selectedProduct) return;
    setGenerating(true); setLastResult(null);
    try {
      const { data } = await API.post("/admin/qr/generate", {
        product_code: selectedProduct.product_code,
        category: selectedProduct.category || "",
        sub_category: selectedProduct.sub_category || "",
        count: Math.max(1, Math.min(100000, parseInt(count) || 1)),
      });
      setLastResult({ ...data, product: selectedProduct });
      setBatches(prev => [{
        batch_id: data.batch_id,
        category: selectedProduct.category,
        sub_category: selectedProduct.sub_category,
        product_code: selectedProduct.product_code,
        product_name: selectedProduct.name,
        count: data.count,
        created_at: new Date().toISOString(),
      }, ...prev]);
    } catch (e) {
      alert(e.response?.data?.detail || "Generation failed");
    } finally { setGenerating(false); }
  };

  const viewBatchCodes = async (batchId) => {
    setViewBatch(batchId); setLoading(true);
    try { const { data } = await API.get(`/admin/qr/batch/${batchId}`); setBatchCodes(data || []); }
    catch {} finally { setLoading(false); }
  };

  const downloadCSV = (batchId) => {
    const csv = "QR String,Product Code,Batch,Serial,MFG Date,EXP Date\n" + batchCodes.map(c =>
      `"${c.qr_string}","${c.product_code}","${c.batch_number}","${c.serial_number}","${c.mfg_date}","${c.exp_date}"`
    ).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `${batchId}.csv`; a.click(); URL.revokeObjectURL(url);
  };

  const presets = [1, 10, 100, 500, 1000];

  // ── Test QR (single code for end-to-end scan validation) ──
  const [testProductCode, setTestProductCode] = useState("120247");
  const [testQrString, setTestQrString] = useState("120247*T032511 : 00130*03/2025*02/2027");
  const [testResult, setTestResult] = useState(null);
  const [testLoading, setTestLoading] = useState(false);
  const [testError, setTestError] = useState("");
  const [copiedQr, setCopiedQr] = useState(false);
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

  const createTestQR = async () => {
    setTestLoading(true); setTestError(""); setTestResult(null);
    try {
      const body = {};
      if (testQrString.trim()) body.qr_string = testQrString.trim();
      else if (testProductCode.trim()) body.product_code = testProductCode.trim();
      else throw new Error("Provide a product code or full QR string.");
      const { data } = await API.post("/admin/qr/test", body);
      setTestResult(data);
    } catch (e) {
      setTestError(e.response?.data?.detail || e.message || "Could not create test QR.");
    } finally { setTestLoading(false); }
  };

  const copyTestQr = () => {
    if (!testResult?.qr_string) return;
    navigator.clipboard.writeText(testResult.qr_string);
    setCopiedQr(true); setTimeout(() => setCopiedQr(false), 1500);
  };

  return (
    <div className="space-y-6" data-testid="admin-qr-generator">
      <div className="flex items-center gap-3">
        <QrCode className="w-6 h-6 text-[#008A99]" />
        <div>
          <h1 className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Calibri, Carlito, "Segoe UI", sans-serif' }}>Generate QR Codes</h1>
          <p className="text-sm text-gray-500">Issue single or bulk QR codes for a reagent. Only issued codes can be scanned to earn points.</p>
        </div>
      </div>

      {/* Test QR — quick single-QR generator for end-to-end scan validation */}
      <div className="bg-amber-50/40 border border-amber-200 rounded-2xl p-5 space-y-3" data-testid="test-qr-panel">
        <div className="flex items-center gap-2">
          <FlaskConical className="w-5 h-5 text-amber-700" />
          <h3 className="font-bold text-amber-900">Quick Test QR</h3>
          <span className="text-[10px] font-black uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-700 text-white">Sandbox</span>
        </div>
        <p className="text-xs text-amber-800/80">Generate a single scannable QR for any reagent. Use this to validate the scan → points pipeline end-to-end. Format: <code className="bg-white/60 px-1 rounded">PRODUCT_CODE*LOT : UNIQUE*MM/YYYY*MM/YYYY</code></p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider text-amber-900/70">Product Code</label>
            <input
              type="text"
              value={testProductCode}
              onChange={e => setTestProductCode(e.target.value)}
              placeholder="e.g. 120247"
              className="mt-1 w-full px-3 py-2 border border-amber-200 rounded-lg text-sm focus:outline-none focus:border-amber-500 bg-white"
              data-testid="test-qr-product-code"
            />
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider text-amber-900/70">Custom QR String <span className="font-normal text-amber-700/70">(optional — auto-generated if blank)</span></label>
            <input
              type="text"
              value={testQrString}
              onChange={e => setTestQrString(e.target.value)}
              placeholder="120247*T032511 : 00130*03/2025*02/2027"
              className="mt-1 w-full px-3 py-2 border border-amber-200 rounded-lg text-sm focus:outline-none focus:border-amber-500 bg-white font-mono"
              data-testid="test-qr-string"
            />
          </div>
        </div>
        <button
          onClick={createTestQR}
          disabled={testLoading || (!testProductCode.trim() && !testQrString.trim())}
          className="px-4 py-2 bg-amber-600 text-white rounded-lg text-sm font-bold hover:bg-amber-700 disabled:bg-gray-300 flex items-center gap-2"
          data-testid="test-qr-create-btn"
        >
          {testLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <FlaskConical className="w-4 h-4" />}
          Generate & Activate Test QR
        </button>
        {testError && <p className="text-xs text-red-600" data-testid="test-qr-error">{testError}</p>}
        {testResult && (
          <div className="mt-2 bg-white rounded-xl border border-amber-200 p-4 flex flex-col md:flex-row gap-4 items-start" data-testid="test-qr-result">
            <img
              src={`${BACKEND_URL}${testResult.image_url}`}
              alt={testResult.qr_string}
              className="w-40 h-40 flex-shrink-0 border-2 border-amber-200 rounded-xl bg-white p-2"
              data-testid="test-qr-image"
            />
            <div className="flex-1 min-w-0 space-y-2 text-sm">
              <div>
                <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400">QR String</p>
                <div className="flex items-center gap-2">
                  <code className="text-xs bg-slate-100 px-2 py-1 rounded text-slate-800 break-all flex-1" data-testid="test-qr-string-out">{testResult.qr_string}</code>
                  <button onClick={copyTestQr} className="text-xs text-[#008A99] hover:bg-[#E0F4F5] p-1 rounded" data-testid="test-qr-copy">
                    {copiedQr ? <Check className="w-3.5 h-3.5 text-green-600" /> : <Copy className="w-3.5 h-3.5" />}
                  </button>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
                <div><span className="text-gray-400">Product</span><p className="font-bold text-gray-800">{testResult.product_name}</p></div>
                <div><span className="text-gray-400">Code</span><p className="font-bold text-gray-800">{testResult.product_code}</p></div>
                <div><span className="text-gray-400">MRP</span><p className="font-bold text-[#008A99]">₹{testResult.mrp}</p></div>
                <div><span className="text-gray-400">Type</span><p className="font-bold text-gray-800 capitalize">{testResult.reagent_type}</p></div>
              </div>
              <a
                href={`${BACKEND_URL}${testResult.image_url}`}
                download={`test-qr-${testResult.product_code}.png`}
                className="inline-flex items-center gap-1.5 text-xs font-bold text-amber-700 hover:underline"
                data-testid="test-qr-download"
              >
                <Download className="w-3.5 h-3.5" /> Download PNG
              </a>
              <p className="text-[10px] text-gray-500 leading-snug">
                Points awarded on scan = <b>MRP × tier's Open/Close %</b>. If the customer's tier has Open/Close % set to <b>0</b>,
                this scan will succeed but credit <b>0 points</b> — adjust the tier in Admin → Config to test point accrual.
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Step 1 — Pick reagent */}
      <div className="bg-white border rounded-xl p-5 space-y-4">
        <div>
          <h3 className="font-semibold text-gray-900 mb-2">1. Pick Reagent</h3>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={productSearch}
              onChange={e => setProductSearch(e.target.value)}
              placeholder="Search by code, name or segment…"
              className="w-full pl-10 pr-4 py-2.5 border rounded-xl text-sm focus:outline-none focus:border-[#008A99]"
              data-testid="qr-product-search"
            />
          </div>
          {selectedProduct && (
            <div className="mt-3 px-3 py-2.5 bg-[#E0F4F5] border border-[#008A99]/30 rounded-xl flex items-center justify-between" data-testid="qr-selected-product">
              <div className="min-w-0">
                <p className="text-[10px] uppercase tracking-wider font-bold text-[#008A99]">Selected Reagent</p>
                <p className="text-sm font-semibold text-gray-900 truncate">{selectedProduct.name}</p>
                <p className="text-[10px] text-gray-500 font-mono">{selectedProduct.product_code} · {selectedProduct.category} · {selectedProduct.reagent_type}</p>
              </div>
              <button onClick={() => setSelectedProduct(null)} className="text-xs text-gray-500 hover:text-red-500 px-2">Clear</button>
            </div>
          )}
          {!selectedProduct && filtered.length > 0 && (
            <div className="mt-2 max-h-56 overflow-y-auto border rounded-xl divide-y bg-white" data-testid="qr-product-list">
              {filtered.map(p => (
                <button key={`${p.product_code}-${p.reagent_type}`} onClick={() => setSelectedProduct(p)}
                  className="w-full text-left px-3 py-2 hover:bg-slate-50 transition-colors"
                  data-testid={`qr-product-option-${p.product_code}`}>
                  <p className="text-sm font-medium text-gray-900 truncate">{p.name}</p>
                  <p className="text-[10px] text-gray-400 font-mono">{p.product_code} · {p.category}</p>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Step 2 — Quantity */}
        <div>
          <h3 className="font-semibold text-gray-900 mb-2">2. Quantity</h3>
          <div className="flex flex-wrap items-center gap-2">
            {presets.map(n => (
              <button key={n} onClick={() => setCount(n)}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold border ${count == n ? "bg-[#008A99] text-white border-[#008A99]" : "bg-white text-gray-600 border-gray-200 hover:border-[#008A99]"}`}
                data-testid={`qr-preset-${n}`}>
                {n.toLocaleString()}
              </button>
            ))}
            <div className="flex items-center gap-2 ml-auto">
              <span className="text-xs text-gray-500">Custom</span>
              <input type="number" value={count} min={1} max={100000}
                onChange={e => setCount(Math.max(1, Math.min(100000, parseInt(e.target.value) || 1)))}
                className="w-28 px-3 py-1.5 border rounded-lg text-sm focus:outline-none focus:border-[#008A99]"
                data-testid="qr-count-input" />
            </div>
          </div>
          <p className="text-[10px] text-gray-400 mt-1.5">Max 100,000 QR codes per batch. Each QR code is single-use.</p>
        </div>

        <button onClick={generateQR} disabled={generating || !selectedProduct}
          className="w-full md:w-auto flex items-center gap-2 px-6 py-2.5 bg-[#008A99] hover:bg-[#006670] text-white rounded-xl text-sm font-semibold disabled:opacity-50 shadow-lg shadow-[#008A99]/20" data-testid="generate-qr-btn">
          {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
          Generate {count.toLocaleString()} QR Code{count == 1 ? "" : "s"}
        </button>
      </div>

      {/* Last result toast */}
      {lastResult && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 flex items-center justify-between" data-testid="qr-last-result">
          <div>
            <p className="text-sm font-semibold text-emerald-700">
              ✅ Generated {lastResult.count.toLocaleString()} QR{lastResult.count == 1 ? "" : "s"} for <span className="font-bold">{lastResult.product?.name}</span>
            </p>
            <p className="text-[11px] text-emerald-600 font-mono mt-0.5">Batch: {lastResult.batch_id}</p>
          </div>
          <button onClick={() => viewBatchCodes(lastResult.batch_id)} className="text-xs px-3 py-1.5 bg-white text-[#008A99] border border-[#008A99]/30 rounded-lg font-semibold flex items-center gap-1">
            <Eye className="w-3 h-3" /> Preview
          </button>
        </div>
      )}

      {/* Batch Preview */}
      {viewBatch && (
        <div className="bg-white border rounded-xl p-5 space-y-4" data-testid="batch-preview">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-gray-900">Batch: {viewBatch}</h3>
            <div className="flex gap-2">
              <button onClick={() => downloadCSV(viewBatch)} className="px-4 py-2 bg-green-50 text-green-600 rounded-lg text-xs font-semibold flex items-center gap-1" data-testid="download-csv-btn"><Download className="w-3 h-3" /> Download CSV</button>
              <button onClick={() => { setViewBatch(null); setBatchCodes([]); }} className="px-4 py-2 bg-gray-100 text-gray-600 rounded-lg text-xs font-semibold">Close</button>
            </div>
          </div>
          {loading ? <Loader2 className="w-5 h-5 animate-spin text-[#008A99] mx-auto" /> : (
            <div className="max-h-72 overflow-y-auto">
              <table className="w-full text-xs">
                <thead><tr className="bg-gray-50 border-b">
                  <th className="px-3 py-2 text-left">QR String</th>
                  <th className="px-3 py-2 text-left">Product</th>
                  <th className="px-3 py-2 text-left">Batch</th>
                  <th className="px-3 py-2 text-left">MFG</th>
                  <th className="px-3 py-2 text-left">EXP</th>
                </tr></thead>
                <tbody>{batchCodes.map((c, i) => (
                  <tr key={i} className="border-b">
                    <td className="px-3 py-2 font-mono text-[10px]">{c.qr_string}</td>
                    <td className="px-3 py-2">{c.product_code}</td>
                    <td className="px-3 py-2">{c.batch_number}</td>
                    <td className="px-3 py-2">{c.mfg_date}</td>
                    <td className="px-3 py-2">{c.exp_date}</td>
                  </tr>
                ))}</tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Batch History */}
      <div>
        <h3 className="font-semibold text-gray-900 mb-3">Generated Batches</h3>
        <div className="space-y-2">
          {batches.map(b => (
            <div key={b.batch_id || b.id} className="bg-white border rounded-xl p-4 flex items-center justify-between" data-testid="batch-item">
              <div className="min-w-0 pr-3">
                <p className="font-mono text-sm font-bold text-[#008A99] truncate">{b.batch_id}</p>
                <p className="text-xs text-gray-500 truncate">{b.product_name || b.product_code} · {b.category || ""}{b.sub_category ? ` / ${b.sub_category}` : ""} · {b.count} codes · {new Date(b.created_at).toLocaleDateString()}</p>
              </div>
              <div className="flex gap-2 flex-shrink-0">
                <button onClick={() => viewBatchCodes(b.batch_id)} className="px-3 py-1.5 bg-[#E0F4F5] text-[#008A99] rounded-lg text-xs font-semibold flex items-center gap-1" data-testid="preview-batch-btn"><Eye className="w-3 h-3" /> Preview</button>
              </div>
            </div>
          ))}
          {batches.length === 0 && <p className="text-center text-gray-400 py-4 text-sm">No batches generated yet</p>}
        </div>
      </div>
    </div>
  );
}
